import React, { Component } from "react";
import { connect } from "react-redux";
import { getLocalISODate, apiRequest, downloadFile as fileDownload, getLocaleDate } from "../../../../utils/Helpers";
import { Wrapper, Row } from "../../../atoms/Layout";
import DatePickerV2 from "../../../atoms/DatePickerV2";
import Link from "../../../atoms/Link";
import appConfig from "../../../../../config/config.dev";
import { createAlert } from "../../../../actions/utils";
import Button from "../../../atoms/Button";

interface ECommunicationEmailPerformanceProps {
    clients: Mongo.client[];
    user: Mongo.clientAdmin;
}

interface ECommunicationEmailPerformanceState {
    clients: Mongo.client[];
    startDate: string;
    endDate: string;
    tomorrow: string;
    ovLoading?: boolean;
    subLoading?: boolean;
    clientCode: string;
    renderData: boolean;
}

class ECommunicationEmailPerformance extends Component<ECommunicationEmailPerformanceProps, ECommunicationEmailPerformanceState> {
    constructor(props) {
        super(props);
        const end = new Date();
        const start = new Date();
        const tomorrow = new Date();
        start.setMonth(start.getMonth() - 1);
        tomorrow.setDate(tomorrow.getDate() + 2);
        this.state = {
            clients: props.user.isSuper ? Object.keys(props.clients || {}).map(cid => { if (props.clients[cid].allowECommunication) return props.clients[cid]; }).filter(client => client != null) : [],
            startDate: `${getLocalISODate(start)}`,
            endDate: `${getLocalISODate(end)}`,
            tomorrow: `${getLocalISODate(tomorrow)}`,
            ovLoading: false,
            subLoading: false,
            clientCode: props.user.cid || "",
            renderData: false
        };
        this._buildQueryFilters = this._buildQueryFilters.bind(this);
        this._onFieldChange = this._onFieldChange.bind(this);
        this._clearFilters = this._clearFilters.bind(this);
        this._renderHeader = this._renderHeader.bind(this);
        this._renderMessagePage = this._renderMessagePage.bind(this);
        this._downloadEnhanceDashboard = this._downloadEnhanceDashboard.bind(this);
    }

    _buildQueryFilters(props) {
        const queryFilters = {} as dynamic;
        if (props.startDate) {
            queryFilters.sd = props.startDate.substr(0, 10);
            // Important to and Thh:mm:ss.SSS since without the time specified it will assume UTC midnight instead of local
            queryFilters.sdtz = new Date(queryFilters["sd"] + "T00:00:00.000").getTimezoneOffset();
        }
        if (props.endDate) {
            queryFilters.ed = props.endDate.substr(0, 10);
            // Important to set time to end of local day since days on timezone transitions will have different timezone at end of day vs beginning
            queryFilters.edtz = new Date(queryFilters["ed"] + "T23:59:59.999").getTimezoneOffset();
        }
        if (props.clientCode) {
            queryFilters.clientCode = props.clientCode;
        }
        return queryFilters;
    }

    _onFieldChange(field, val) {
        const value = val && val.target ? val.target.value : val;
        this.setState({ ...this.state, [field]: value });
    }

    _clearFilters() {
        const end = new Date();
        const start = new Date();
        const tomorrow = new Date();
        start.setMonth(start.getMonth() - 1);
        tomorrow.setDate(tomorrow.getDate() + 1);

        this.setState({
            startDate: `${getLocalISODate(start)}`,
            endDate: `${getLocalISODate(end)}`,
            tomorrow: `${getLocalISODate(tomorrow)}`,
            clientCode: this.props.user.cid || "",
            renderData: false,
        });
        createAlert('Filters have been cleared', 'success')
    }

    async _downloadEnhanceDashboard():  Promise<void> {
        return new Promise(async (resolve, reject) => {
            const filters = this._buildQueryFilters(this.state);
            await apiRequest(`${appConfig.API_URL}/e-communication/reports/emailPerformance`, "POST", filters).then(res => {
                if (res?.length == 0) {
                    createAlert("No data available", "error");
                    resolve();
                }
                const headers = ["From Email", "DeliveryOn", "Title", "Subject", "Type", "Sends", "Reads", "Clicks", "Unsubscribes"];
                let data = '"' + headers.join("\",\"") + '"';
                for (const record of res) {
                    let row = "\n\"" + record["From Email"] + "\"";
                    for (let idx = 1; idx < headers.length; idx++) {
                        switch (headers[idx]) {
                            case "Sends":
                            case "Reads":
                            case "Clicks":
                            case "Unsubscribes":
                                row += ",\"" + record[headers[idx]] + "\"";
                                break;
                            default:
                                row += "," + record[headers[idx]];
                                break;
                        }
                    }
                    data += row;
                }
                fileDownload(data, `enhanceDashboard-${getLocaleDate(new Date())}.csv`);
                resolve();
            }).catch(error => {
                createAlert(error, `error`);
                resolve();
            });
        });
    }

    _renderHeader() {
        const { startDate, endDate, tomorrow, clients, clientCode } = this.state;
        return (
            <div
                className="col-lg-9"
            >
                <div className="form-group">
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            flexDirection: "row",
                            flexWrap: "wrap",
                            marginBottom: "10px"
                        }}
                    >
                        <div className="date__wrapper">
                            <label className="control-label">Start Date</label>
                            <DatePickerV2
                                dateFormat="yyyy/MM/dd"
                                maxDate={endDate}
                                value={startDate}
                                onChange={value => {
                                    if (!value) {
                                        const start = new Date();
                                        start.setMonth(start.getMonth() - 1);
                                        value = `${getLocalISODate(start)}T00:00:00.000Z`;
                                    }
                                    this._onFieldChange("startDate", value);
                                }}
                                onBlur={() => this._onFieldChange("startDate", "")}
                                enableTabLoop={false}
                            />
                        </div>
                        <div
                            className="date__wrapper"
                            style={{
                                marginLeft: "10px"
                            }}
                        >
                            <label className="control-label">End Date</label>
                            <DatePickerV2
                                dateFormat="yyyy/MM/dd"
                                maxDate={tomorrow}
                                minDate={startDate}
                                value={endDate}
                                onChange={value => {
                                    if (!value) {
                                        value = `${getLocalISODate(new Date())}T00:00:00.000Z`;
                                    }
                                    this._onFieldChange("endDate", value);
                                }}
                                onBlur={() => this._onFieldChange("endDate", "")}
                                enableTabLoop={false}
                            />
                        </div>
                        {
                            this.props.user.isSuper && (
                                <div
                                    style={{
                                        marginLeft: "10px"
                                    }}
                                >
                                    <label className="control-label">Client</label>
                                    <div
                                        className="select__wrapper"
                                        style={{
                                            marginLeft: "0px"
                                        }}
                                    >
                                        <select
                                            value={clientCode}
                                            onChange={event =>
                                                this._onFieldChange("clientCode", event.target.value)
                                            }
                                        >
                                            <option value="">Select a client</option>
                                            {
                                                clients &&
                                                clients.map(
                                                    client => {
                                                        return (
                                                            <option key={client.cid} value={client.cid}>
                                                                {client.name}
                                                            </option>
                                                        );
                                                    }
                                                )
                                            }
                                        </select>
                                    </div>
                                </div>
                            )
                        }
                        <div>
                            <Button
                                onClick={() => this._downloadEnhanceDashboard()}
                                style={{ margin: "10px", top: "10px" }}
                            >
                                Download
                            </Button>
                        </div>
                        <div>
                            <label className="" />
                            <div style={{ margin: "0 10px 0 10px" }}>
                                <Link style={{ textDecoration: "underline", color: "#152733" }}
                                    onClick={() => this._clearFilters()}
                                >Clear Filter</Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    _renderMessagePage() {
        return (
            <div style={{ width: "100%", display: "block", marginTop: "14%" }}>
                <p style={{ fontSize: 20, textAlign: "center", alignSelf: "end", fontWeight: "bold" }}>To run this report, please adjust the filters above and click "Download"</p>
                <p style={{ textAlign: "center", opacity: 0.5 }}>
                    <img src="/img/Reports_Load.svg" style={{ color: "#A7B1C2", opacity: 0.5 }} />
                </p>
            </div>
        );
    }

    render() {
        const { ovLoading, subLoading, renderData } = this.state;
        return (
            <Wrapper id="ecomm-dashboard">
                <Row>
                    {this._renderHeader()}
                    {!(ovLoading && subLoading) && !renderData && this._renderMessagePage()}
                </Row>
            </Wrapper>
        );
    }
}

const mapStateToProps = state => ({
    clients: state.lists.clients,
    user: state.session.admin
});

export default connect(mapStateToProps)(ECommunicationEmailPerformance);
